import { Box, Flex, FloraStepper, FloraTypography } from '@grupoboticario/flora-react'

import {
  ACCELERATION_STEP_INDEX,
  ACCELERATION_STEP_LABELS,
  ACCELERATION_STEPPER_VARIANT
} from '@/constants/accelerationStepper'
import { FadeIn } from '@/utils/useFadeIn/useFadeIn'

import HeadingAccelerator from '../HeadingAccelerator/HeadingAccelerator'
import type { AccelerationStepperProps } from './AccelerationForm.types'
import StepActions from './StepActions'
import StepDesign from './steps/StepDesign'
import StepDetails from './steps/StepDetails'
import StepScoring from './steps/StepScoring'
import { useAccelerationStepperForm } from './useAccelerationStepperForm'
import { hasCampaignChanges } from './utils/getCampaignDiff'

const AccelerationStepper = ({
  initialValues,
  variant = ACCELERATION_STEPPER_VARIANT.CREATE,
  isEditing = false,
  stepReadOnly: stepReadOnlyProp,
  onPublish,
  onCancel
}: AccelerationStepperProps) => {
  const {
    currentStep,
    setCurrentStep,
    formState,
    previewImageDataUrl,
    setPreviewImageDataUrl,
    resolvedStepReadOnly,
    isLastStep,
    isCurrentStepValid,
    canGoBack,
    steps,
    handleTopFieldChange,
    handleSegmentationChange,
    handlePointsChange,
    handleStartDateChange,
    handleEndDateChange,
    handleValidityStartDateChange,
    handleMultiplierChange,
    handleTypeChange,
    patchContentScalar,
    patchDetailsField,
    handleBrandChange,
    handleBrandButtonUrlChange,
    handleStepperStepClick
  } = useAccelerationStepperForm({
    initialValues,
    variant,
    isEditing,
    stepReadOnly: stepReadOnlyProp
  })

  let canPublish = true
  if (variant === ACCELERATION_STEPPER_VARIANT.EDIT && initialValues && isLastStep) {
    canPublish = hasCampaignChanges(initialValues, formState)
  }

  return (
    <FadeIn>
      <Flex gap="$14">
        <Box>
          <FloraStepper steps={steps} direction="column" onStepClick={handleStepperStepClick} />
        </Box>

        <Box css={{ flex: 1 }}>
          <Box css={{ marginTop: '$6' }}>
            <FloraTypography
              fontSize="bodySmallShort"
              fontWeight="medium"
              css={{ color: '$nonInteractiveAuxiliar', marginBottom: '$2' }}
            >
              Etapa {currentStep + 1} de {steps.length}
            </FloraTypography>

            {currentStep !== ACCELERATION_STEP_INDEX.POINTS && (
              <HeadingAccelerator title={ACCELERATION_STEP_LABELS[currentStep].text} />
            )}

            {currentStep === ACCELERATION_STEP_INDEX.POINTS && (
              <StepScoring
                values={formState}
                isReadOnly={resolvedStepReadOnly.configuration}
                onChange={handleTopFieldChange}
                onSegmentationChange={handleSegmentationChange}
                onPointsChange={handlePointsChange}
                onStartDateChange={handleStartDateChange}
                onEndDateChange={handleEndDateChange}
                onValidityStartDateChange={handleValidityStartDateChange}
                onMultiplierChange={handleMultiplierChange}
                onTypeChange={handleTypeChange}
                initialValues={initialValues}
                variant={variant}
              />
            )}

            {currentStep === ACCELERATION_STEP_INDEX.VISUAL && (
              <StepDesign
                values={formState.content}
                brand={formState.brand}
                contentByBrand={formState.contentByBrand}
                readOnly={resolvedStepReadOnly.visual}
                previewImageDataUrl={previewImageDataUrl}
                onPreviewImageDataUrlChange={setPreviewImageDataUrl}
                onChange={(field, value) => patchContentScalar(field, value)}
                onShowModalChange={(v) => patchContentScalar('showModal', v)}
                onBrandChange={handleBrandChange}
                onBrandButtonUrlChange={handleBrandButtonUrlChange}
                variant={variant}
              />
            )}

            {currentStep === ACCELERATION_STEP_INDEX.DETAILS && (
              <StepDetails
                values={formState.content.details}
                readOnly={resolvedStepReadOnly.details}
                onChange={(field, value) => patchDetailsField(field, value)}
              />
            )}
          </Box>

          <StepActions
            variant={variant}
            isLastStep={isLastStep}
            canGoBack={canGoBack}
            isStepValid={isCurrentStepValid}
            canPublish={canPublish}
            onBack={() => setCurrentStep((s) => s - 1)}
            onNext={() => setCurrentStep((s) => s + 1)}
            onPublish={onPublish ? () => onPublish(formState) : undefined}
            onCancel={onCancel}
          />
        </Box>
      </Flex>
    </FadeIn>
  )
}

export default AccelerationStepper
